/**
 * Store mínimo de la **sede activa**.
 *
 * La sede activa no es dato de servidor (no va en el cache de TanStack Query):
 * es estado de UI (ver `docs/frontend_architecture.md`, Pr. 4). El cliente
 * `lib/api` la lee de forma **síncrona** en su hook `beforeRequest` para
 * inyectar el header `X-Sede-Id`; por eso vive en un store de módulo y no solo
 * en un Context de React (un interceptor de `ky` no puede usar hooks).
 *
 * Por ahora es un store ligero con suscripción (apto para `useSyncExternalStore`
 * en features futuras). Guarda **solo** el id de sede: una preferencia de UI no
 * sensible. Nunca credenciales ni token (`docs/frontend_architecture.md`, Pr. 3).
 */

type SedeId = number | null;
type Listener = () => void;

let activeSedeId: SedeId = null;
const listeners = new Set<Listener>();

/** Devuelve el id de la sede activa, o `null` si aún no se ha elegido. */
export function getActiveSedeId(): SedeId {
  return activeSedeId;
}

/** Fija la sede activa y notifica a los suscriptores. */
export function setActiveSedeId(sedeId: SedeId): void {
  if (sedeId === activeSedeId) {
    return;
  }
  activeSedeId = sedeId;
  for (const listener of listeners) {
    listener();
  }
}

/** Suscribe un listener a los cambios de sede. Devuelve la función de baja. */
export function subscribeSede(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
